import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { fetchBySlug } from '../lib/cms';
import useDocumentMeta from '../hooks/useDocumentMeta';
import { Heart, Users, Sparkles, Award, Shield, Compass, ArrowRight, Scale, Medal, PawPrint, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';

function getCdnUrl(url, width = 800, quality = 80) {
  if (!url || !url.includes('assets.auxodev.dk')) return url;
  if (url.includes('/cdn-cgi/image/')) return url;
  return url.replace('https://assets.auxodev.dk/', `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=${quality},format=auto/`);
}

export default function Vaerdigrundlag() {
  useDocumentMeta(
    'Værdigrundlag | LIVSLYST ApS',
    'Vores værdigrundlag er fundamentet for alt, hvad vi gør, og sikrer en stabil, tryg og positiv hverdag for alle.'
  );

  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadPage() {
      const data = await fetchBySlug('da', 'vaerdigrundlag');
      setPageData(data);
      setLoading(false);
    }
    loadPage();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh] bg-stone-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  const title = pageData?.title?.da || pageData?.title || 'Kerneværdier';
  const intro = pageData?.intro?.da || pageData?.intro || 'Hos Livslyst ApS tror vi på, at alle mennesker – uanset baggrund, forudsætninger og livssituation – har ret til at være en del af et meningsfuldt fællesskab.';

  const vaerdiBlokke = [
    {
      title: 'Trivsel, udvikling & inkluderende arbejdsfællesskab',
      badge: 'Relationer & Trivsel',
      paragraphs: [
        'Vi tror på, at trivsel og udvikling skabes gennem trygge relationer, forudsigelige rammer og meningsfulde aktiviteter. Derfor tager vores pædagogiske tilgang udgangspunkt i den enkelte borgers ressourcer, interesser og udviklingspotentiale.',
        'I Livslyst ønsker vi at skabe et trygt og inkluderende arbejdsfællesskab, hvor alle oplever at have værdi og betydning. Gennem medbestemmelse, ansvar og deltagelse i daglige arbejdsopgaver styrkes borgerens selvværd, motivation og oplevelse af at bidrage til et fællesskab.'
      ],
      bullets: [
        'At blive mødt med respekt og anerkendelse',
        'At opleve struktur og forudsigelighed i hverdagen',
        'At få mulighed for medbestemmelse',
        'At opleve sig som en vigtig del af fællesskabet',
        'At udvikle selvværd og tro på egne evner'
      ],
      image: 'https://assets.auxodev.dk/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/fullsizerender3.jpeg',
      icon: Users,
      color: 'emerald'
    },
    {
      title: 'Læringsmiljø med dyr',
      badge: 'Dyr & Natur',
      paragraphs: [
        'En central del af vores læringsmiljø er arbejdet med dyr. Vi oplever, at ansvar for dyr kan styrke empati, omsorg, struktur og følelsen af at være nødvendig. Når borgeren oplever, at deres indsats har betydning for andre levende væsener, understøttes både trivsel, ansvarsfølelse og personlig udvikling.'
      ],
      bullets: [
        'Ansvarsfølelse',
        'Empati og omsorg',
        'Struktur og rutiner',
        'Ro og nærvær',
        'Oplevelsen af at være betydningsfuld'
      ],
      image: 'https://assets.auxodev.dk/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/img-7033.png',
      icon: PawPrint,
      color: 'amber'
    },
    {
      title: 'Individuelt tilrettelagte arbejdsopgaver',
      badge: 'Meningsfuld Beskæftigelse',
      paragraphs: [
        'Vi tror på, at mennesker udvikler sig bedst, når they arbejder med noget, der giver mening og vækker interesse. Derfor tilrettelægges arbejdsopgaverne individuelt med fokus på succesoplevelser, udvikling og deltagelse i fællesskabet.'
      ],
      bullets: [
        'Inddrages aktivt i hverdagen',
        'Arbejder ud fra egne ressourcer og interesser',
        'Oplever succesoplevelser og progression',
        'Udvikler sociale og personlige kompetencer',
        'Får mulighed for deltagelse i meningsfulde fællesskaber'
      ],
      image: 'https://assets.auxodev.dk/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/img-7724.jpeg',
      icon: Sparkles,
      color: 'teal'
    }
  ];

  return (
    <div className="bg-stone-50 text-stone-800 antialiased overflow-x-hidden">
      {/* 1. Luksuriøs Hero-sektion */}
      <section className="relative bg-gradient-to-b from-stone-100/90 via-stone-50 to-white py-20 md:py-28 border-b border-stone-200 overflow-hidden">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-emerald-100/40 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-amber-100/40 blur-3xl" />
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 text-emerald-800 text-xs font-semibold uppercase tracking-wider"
          >
            <Compass className="w-4 h-4 text-emerald-600 animate-spin-slow" />
            <span>Vores Fundament</span>
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-stone-900 tracking-tight leading-tight"
          >
            {title}
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg md:text-xl text-stone-600 max-w-3xl mx-auto leading-relaxed font-medium"
          >
            {intro}
          </motion.p>
        </div>
      </section>

      {/* 2. De tre pædagogiske værdiblokke */}
      {vaerdiBlokke.map((block, idx) => {
        const Icon = block.icon;
        const isEven = idx % 2 === 0;
        
        // Color themes
        const themeClasses = {
          emerald: {
            badge: 'bg-emerald-50 text-emerald-800 border-emerald-100',
            iconBg: 'bg-emerald-50 text-emerald-800 border-emerald-100',
            bulletBg: 'bg-emerald-50 text-emerald-800 border-emerald-100',
            bg: 'bg-white'
          },
          amber: {
            badge: 'bg-amber-50 text-amber-800 border-amber-100',
            iconBg: 'bg-amber-50 text-amber-800 border-amber-100',
            bulletBg: 'bg-amber-50 text-amber-800 border-amber-100',
            bg: 'bg-stone-50/50'
          },
          teal: {
            badge: 'bg-teal-50 text-teal-800 border-teal-100',
            iconBg: 'bg-teal-50 text-teal-800 border-teal-100',
            bulletBg: 'bg-teal-50 text-teal-800 border-teal-100',
            bg: 'bg-white'
          }
        }[block.color];

        return (
          <section 
            key={idx} 
            className={`py-16 md:py-24 border-b border-stone-200 ${themeClasses.bg}`}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                {/* Text Column */}
                <motion.div 
                  initial={{ opacity: 0, x: isEven ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className={`lg:col-span-7 space-y-6 ${isEven ? 'lg:order-first' : 'lg:order-last'}`}
                >
                  <div className="space-y-4">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${themeClasses.badge}`}>
                      <Icon className="w-3.5 h-3.5" />
                      {block.badge}
                    </span>
                    <h2 className="text-3xl md:text-4xl font-bold text-stone-900 tracking-tight">
                      {block.title}
                    </h2>
                  </div>

                  <div className="text-stone-600 leading-relaxed space-y-4 text-base md:text-lg">
                    {block.paragraphs.map((p, pIdx) => (
                      <p key={pIdx}>{p}</p>
                    ))}
                  </div>

                  {/* Bullet Points */}
                  <div className="pt-4">
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {block.bullets.map((bullet, bIdx) => (
                        <li 
                          key={bIdx} 
                          className="flex items-start gap-3 bg-stone-50/80 p-3.5 rounded-2xl border border-stone-200/60 text-stone-700 text-sm font-medium transition-all hover:bg-stone-50"
                        >
                          <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold mt-0.5 border ${themeClasses.bulletBg}`}>
                            ✓
                          </span>
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>

                {/* Image Column */}
                <motion.div 
                  initial={{ opacity: 0, x: isEven ? 30 : -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className="lg:col-span-5"
                >
                  <div className="relative">
                    <div className="absolute inset-0 bg-stone-800 rounded-3xl rotate-2 scale-95 opacity-5" />
                    <img
                      src={getCdnUrl(block.image, 800)}
                      alt={block.title}
                      className="relative z-10 w-full h-[380px] md:h-[450px] object-cover rounded-3xl shadow-md border border-stone-200"
                    />
                  </div>
                </motion.div>
              </div>
            </div>
          </section>
        );
      })}

      {/* 4. Livslyst – et RUM for alle */}
      <section className="py-20 md:py-28 bg-white border-b border-stone-200 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-emerald-50 blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-bold">
              Vores Værdikoncept
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 tracking-tight">
              Livslyst – et RUM for alle
            </h2>
            <p className="text-stone-600 text-base md:text-lg leading-relaxed">
              Hos Livslyst ApS arbejder vi ud fra vores kerneværdier, samlet i sloganet: <strong className="text-emerald-800">“Livslyst – et RUM for alle”</strong>. RUM symboliserer de værdier, som danner grundlag for vores pædagogiske arbejde og menneskesyn.
            </p>
          </div>

          {/* RUM Columns */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {/* R – Relationer */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-stone-50/60 hover:bg-white p-8 rounded-3xl border border-stone-200 border-t-4 border-t-emerald-600 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between relative overflow-hidden group"
            >
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="p-3.5 rounded-2xl bg-emerald-50 text-emerald-800 font-extrabold text-2xl w-14 h-14 flex items-center justify-center shadow-inner border border-emerald-100">
                    R
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-stone-900">Relationer</h3>
                    <span className="text-xs text-emerald-700 font-semibold uppercase tracking-wider">Fundamentet</span>
                  </div>
                </div>

                <p className="text-stone-600 text-sm leading-relaxed">
                  Vi tror på, at trygge og anerkendende relationer er fundamentet for trivsel, udvikling og læring. Derfor møder vi alle borgere med respekt, nærvær og ligeværdighed.
                </p>
                <p className="text-stone-600 text-sm leading-relaxed font-medium">
                  Gennem positive relationer skaber vi tryghed, tillid og tilhørsforhold.
                </p>

                <div className="pt-4 border-t border-stone-200/60 space-y-3">
                  <p className="text-xs font-bold text-stone-500 uppercase tracking-wider">Fokusområder:</p>
                  <ul className="space-y-2.5">
                    {[
                      'At opleve stabile relationer',
                      'At føle sig set og anerkendt',
                      'At opleve tryghed og tillid',
                      'At føle tilhørsforhold til fællesskabet'
                    ].map((bullet, i) => (
                      <li key={i} className="flex items-start gap-3 text-stone-700 text-sm">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-50 text-emerald-800 flex items-center justify-center text-xs font-bold mt-0.5 border border-emerald-100">✓</span>
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>

            {/* U – Udviklende fællesskaber */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-stone-50/60 hover:bg-white p-8 rounded-3xl border border-stone-200 border-t-4 border-t-amber-500 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between relative overflow-hidden group"
            >
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="p-3.5 rounded-2xl bg-amber-50 text-amber-800 font-extrabold text-2xl w-14 h-14 flex items-center justify-center shadow-inner border border-amber-100">
                    U
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-stone-900">Udviklende fællesskaber</h3>
                    <span className="text-xs text-amber-700 font-semibold uppercase tracking-wider">Deltagelse</span>
                  </div>
                </div>

                <p className="text-stone-600 text-sm leading-relaxed">
                  Vi tror på, at mennesker udvikler sig gennem deltagelse i fællesskaber. Hos Livslyst ønsker vi at skabe inkluderende og udviklende fællesskaber, hvor der er plads til forskellighed, medbestemmelse og deltagelse.
                </p>
                <p className="text-stone-600 text-sm leading-relaxed font-medium">
                  Fællesskabet skal styrke den enkeltes ressourcer, sociale kompetencer og oplevelse af at høre til.
                </p>

                <div className="pt-4 border-t border-stone-200/60 space-y-3">
                  <p className="text-xs font-bold text-stone-500 uppercase tracking-wider">Fokusområder:</p>
                  <ul className="space-y-2.5">
                    {[
                      'Mulighed for aktiv deltagelse',
                      'Oplevelse af fællesskab og tilhørsforhold',
                      'Udvikling af sociale kompetencer',
                      'Respekt for individuelle forskelligheder'
                    ].map((bullet, i) => (
                      <li key={i} className="flex items-start gap-3 text-stone-700 text-sm">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-50 text-amber-800 flex items-center justify-center text-xs font-bold mt-0.5 border border-amber-100">✓</span>
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>

            {/* M – Meningsfuldhed og mangfoldighed */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-stone-50/60 hover:bg-white p-8 rounded-3xl border border-stone-200 border-t-4 border-t-sky-500 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between relative overflow-hidden group"
            >
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="p-3.5 rounded-2xl bg-sky-50 text-sky-800 font-extrabold text-2xl w-14 h-14 flex items-center justify-center shadow-inner border border-sky-100">
                    M
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-stone-900">Meningsfuldhed</h3>
                    <span className="text-xs text-sky-700 font-semibold uppercase tracking-wider">Mangfoldighed</span>
                  </div>
                </div>

                <p className="text-stone-600 text-sm leading-relaxed">
                  Vi tror på, at meningsfulde aktiviteter skaber motivation, livsglæde og personlig udvikling. Derfor tager vores arbejdsopgaver udgangspunkt i den enkeltes interesser, ressourcer og potentialer.
                </p>
                <p className="text-stone-600 text-sm leading-relaxed font-medium">
                  Samtidig ser vi mangfoldighed som en styrke og værdsætter de forskellige erfaringer, evner og perspektiver, som hver enkelt bringer med sig.
                </p>

                <div className="pt-4 border-t border-stone-200/60 space-y-3">
                  <p className="text-xs font-bold text-stone-500 uppercase tracking-wider">Fokusområder:</p>
                  <ul className="space-y-2.5">
                    {[
                      'At opleve mening i hverdagen',
                      'At arbejde ud fra egne interesser og styrker',
                      'At opleve motivation og livsglæde',
                      'At blive mødt med respekt for forskellighed'
                    ].map((bullet, i) => (
                      <li key={i} className="flex items-start gap-3 text-stone-700 text-sm">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-sky-50 text-sky-800 flex items-center justify-center text-xs font-bold mt-0.5 border border-sky-100">✓</span>
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Afsluttende grundlag */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="bg-white p-8 md:p-10 rounded-2xl border border-stone-200 shadow-sm relative overflow-hidden"
          >
            <div className="relative z-10 max-w-4xl mx-auto space-y-8">
              <div className="text-center space-y-3">
                <span className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-bold">
                  Afsluttende Grundlag
                </span>
                <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-stone-900">
                  Et trygt, anerkendende og udviklende miljø
                </h3>
              </div>

              <p className="text-stone-600 text-center text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
                Disse værdier er grundlæggende i vores daglige arbejde og er med til at sikre et trygt, anerkendende og udviklende miljø, hvor alle har mulighed for at opleve sig som en værdifuld del af fællesskabet.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center pt-6 border-t border-stone-200">
                <div className="space-y-4">
                  <p className="text-sm font-bold text-emerald-700 uppercase tracking-wider">
                    Hos Livslyst arbejder vi værdimæssigt med meningsfuldhed ind i:
                  </p>
                  <ul className="space-y-3">
                    {[
                      'Livskvalitet',
                      'Selvstændighed',
                      'Deltagelse i fællesskabet'
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-stone-700 text-base">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-stone-500 italic">
                    ...gennem en ressourcebaseret og udviklende hverdag.
                  </p>
                </div>

                <div className="bg-stone-50 border border-stone-200 p-6 rounded-2xl flex flex-col justify-center items-center text-center space-y-4">
                  <p className="text-xl md:text-2xl font-serif italic text-stone-800 leading-relaxed">
                    "Vi ønsker at skabe livsglæde, udvikling og meningsfulde fællesskaber — et RUM for alle."
                  </p>
                  <div className="w-12 h-0.5 bg-emerald-600/60" />
                  <span className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">LIVSLYST ApS</span>
                </div>
              </div>

              <div className="flex justify-center pt-8 border-t border-stone-100">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white font-semibold shadow-md hover:shadow-lg transition-all duration-300 group"
                >
                  <span>Kontakt os for at høre mere</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
